/**
 * One game in Manage Insights — the insight packs that can be opened on it,
 * and the facts the game stands on.
 *
 * A pack is SQL + one HTML page over the serve tables. Opening one builds
 * it the first time (in the request — no job) and serves the stored page
 * after, so there is no Run button, only Open. A pack that cannot be built
 * here says why, by table or by query, in DuckDB's words.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { ApiError, type Session } from '../lib/api'
import { alignToGame } from '../lib/orgAlign'
import { pathSeg } from '../lib/urls'
import {
  listGamePacks, rebuildGameReports, republishPack, setPackPublished,
  type GamePacks, type RequeueResult,
} from '../packs/api'
import {
  publishSummary, publishWhenBuilt, REBUILD_SCOPES, type RebuildScope,
} from '../packs/rebuild'
import GameFactsEditor from './GameFactsEditor'
import { latestPacks } from './LabBuilds'

import './insights-lab.css'
import './insights-packs.css'

export default function InsightsGame({ session }: { session: Session }) {
  const { gameCode } = useParams<{ gameCode: string }>()
  const [packs, setPacks] = useState<GamePacks | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  // Publishing exposes a page to a paying club, so it goes through a confirm
  // modal that holds the pack pending confirmation and its reason — a stray
  // click opens the modal, it does not publish. Rebuild & republish swaps
  // what the club reads, so it confirms the same way.
  const [pending, setPending] = useState<{
    name: string; version: string; mode: 'publish' | 'republish'
  } | null>(null)
  const [reason, setReason] = useState('')
  const [requeueBusy, setRequeueBusy] = useState<'rebuild' | 'publish' | null>(null)
  const [requeued, setRequeued] = useState<RequeueResult | null>(null)
  const [scope, setScope] = useState<RebuildScope>('latest')
  const [published, setPublishedMsg] = useState<string | null>(null)
  // builds left while Rebuild & publish waits
  const [left, setLeft] = useState<number | null>(null)

  const isAdmin = session.user.is_platform_admin

  const load = useCallback(() => {
    if (!gameCode) return
    listGamePacks(gameCode)
      .then(setPacks)
      .catch(async (e: unknown) => {
        // The Lab lists every club's games, but this API is scoped to the
        // club in force — so opening one from another club 404s. Staff reach
        // every club, so that is the wrong club selected, not a refusal:
        // switch into the one that owns the game and reload.
        if ((e as { status?: number }).status === 404) {
          const { switched } = await alignToGame(gameCode)
          if (switched) { window.location.reload(); return }
        }
        setError(e instanceof ApiError ? e.message : String(e))
      })
  }, [gameCode])

  useEffect(() => { load() }, [load])

  const setPublished = (name: string, version: string, next: boolean, why: string) => {
    if (!gameCode) return
    setBusy(`${name}@${version}`)
    setNotice(null)
    setPackPublished(gameCode, name, version, next, why)
      .then(() => load())
      .catch((e: unknown) =>
        setNotice(e instanceof ApiError ? e.message : String(e)))
      .finally(() => { setBusy(null); setPending(null) })
  }

  const republish = (name: string, version: string, why: string) => {
    if (!gameCode) return
    setBusy(`${name}@${version}`)
    setNotice(null)
    republishPack(gameCode, name, version, why)
      .then(() => load())
      .catch((e: unknown) =>
        setNotice(e instanceof ApiError ? e.message : String(e)))
      .finally(() => { setBusy(null); setPending(null) })
  }

  // Rebuild in a scope (packs/rebuild.ts); "& publish" then publishes the
  // latest versions that come out green once the builds finish — the
  // latest only, even after "All versions" (Ben, 2026-10-29).
  const requeue = async (andPublish: boolean) => {
    if (!gameCode || !packs) return
    const latest = latestPacks(packs.pages)
      .map((p) => ({ name: p.name, version: p.version }))
    let reason = ''
    if (andPublish) {
      const r = window.prompt(
        `Rebuild, then publish the green reports on ${gameCode} to the club? Reason:`,
        'rebuild and publish')
      if (r == null) return
      reason = r.trim() || 'rebuild and publish'
    }
    setRequeueBusy(andPublish ? 'publish' : 'rebuild')
    setRequeued(null)
    setPublishedMsg(null)
    setNotice(null)
    try {
      const r = await rebuildGameReports(gameCode, scope === 'failed' ? 'failed' : 'all',
                                         scope === 'all' ? undefined : latest)
      setRequeued(r)
      load()
      if (andPublish && packs.organization_id != null) {
        const p = await publishWhenBuilt({
          orgId: packs.organization_id, gameCode, packs: latest, reason,
          onProgress: setLeft })
        setPublishedMsg(`Builds finished — ${publishSummary(p)}.`)
        load()
      } else if (andPublish) {
        setNotice('Rebuilt, but not published: this game’s club is not known here.')
      }
    } catch (e) {
      setNotice(e instanceof ApiError || e instanceof Error ? e.message : String(e))
    } finally {
      setRequeueBusy(null)
      setLeft(null)
    }
  }

  // Publishing to the club asks first (the confirm modal, below); retiring —
  // reversible, and the lower-risk direction — keeps the inline prompt.
  const onPublishClick = (name: string, version: string, isPublished: boolean) => {
    if (isPublished) {
      const why = window.prompt('Reason for retiring this page?')
      if (why) setPublished(name, version, false, why)
      return
    }
    setReason('')
    setPending({ name, version, mode: 'publish' })
  }

  if (!gameCode) return <div className="panel error">Bad URL.</div>
  if (error) {
    return (
      <div className="insights-packs">
        <div className="panel error">Could not load this game — {error}</div>
        <p><Link to="/manage-insights/games">Back to Manage insights</Link></p>
      </div>
    )
  }
  if (!packs) return <p className="muted">Loading…</p>

  return (
    <div className="insights-packs lab-game-page">
      <header className="insights-packs__head">
        <p>
          <Link to="/manage-insights/games">← Manage insights</Link>
          {' · '}
          <Link to={`/match-room/${pathSeg(gameCode)}`}>Match video →</Link>
          {packs.game_id != null && packs.organization_id != null && (
            <>
              {' · '}
              <Link to={`/ops/layer-stack?game=${packs.game_id}`
                        + `&club=${packs.organization_id}&view=match`}>
                Layer Stack build →
              </Link>
              {' · '}
              <Link to={`/ops/match-lab/${packs.game_id}`}>Match Lab →</Link>
            </>
          )}
        </p>
        <h1 className="mono">{gameCode}</h1>
      </header>

      {notice && <div className="lab-error">{notice}</div>}

      {/* Above the packs: the commonest reason a page differs from the
          artifact it was converted from is a fact in this panel. */}
      <GameFactsEditor gameCode={gameCode} onSaved={load} />

      {isAdmin && packs.served && packs.pages.length > 0 && (
        <section className="requeue">
          <h2>Rebuild reports</h2>
          <p className="lab-sub">
            Queue this game’s pack builds and start the drainers — watch them
            on <Link to="/manage-insights/builds">Builds</Link>. “Latest
            versions only” rebuilds each pack’s newest version; “All versions”
            every version registered; “Failed only” the newest versions not
            already built and current. “Rebuild &amp; publish” publishes the
            newest versions that build green — keep this page open until it
            says so.
          </p>
          <div className="requeue__actions">
            <select value={scope} disabled={requeueBusy !== null}
                    aria-label="What to rebuild"
                    onChange={(e) => setScope(e.target.value as RebuildScope)}>
              {REBUILD_SCOPES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <button type="button" disabled={requeueBusy !== null}
                    onClick={() => void requeue(false)}>
              {requeueBusy === 'rebuild' ? 'Queuing…' : 'Rebuild'}
            </button>
            <button type="button" disabled={requeueBusy !== null}
                    onClick={() => void requeue(true)}>
              {requeueBusy !== 'publish' ? 'Rebuild & publish'
                : `Publishing when built${left === null ? '' : ` (${left} left)`}…`}
            </button>
          </div>
          {published && <p className="muted">{published}</p>}
          {requeued && (
            <p className="muted">
              {requeued.mode === 'all' ? 'Rebuilt' : 'Failed only'}:
              {' '}queued {requeued.queued} of {requeued.packs}
              {requeued.skipped ? `, ${requeued.skipped} already current` : ''}
              {requeued.already_in_flight
                ? `, ${requeued.already_in_flight} already building` : ''}
              {requeued.failures.length
                ? `, ${requeued.failures.length} could not start` : ''}.{' '}
              <Link to="/manage-insights/builds">Watch the queue →</Link>
            </p>
          )}
          {requeued && requeued.failures.length > 0 && (
            <ul className="muted">
              {/* By NAME: a count alone sends the operator hunting
                  through the queue for which pack would not start. */}
              {requeued.failures.map((f) => (
                <li key={f.pack}>
                  <span className="mono">{f.pack}</span> — {f.detail}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <section className="serve-pages">
        <h2>Insight packs</h2>
        {!packs.served && (
          <p className="lab-sub">
            This game is not served from the layer stack here, so no insight
            pack can be built for it.
          </p>
        )}
        {packs.clock_error && (
          <div className="lab-error">
            This game&apos;s clock cannot be read ({packs.clock_error}), so no
            page can be built or judged current until that is fixed.
          </div>
        )}
        {packs.served && packs.pages.length === 0 && (
          <p className="lab-sub">
            No insight pack is registered. Upload one on the registry tab.
          </p>
        )}
        {packs.pages.map((pg) => {
          const run = pg.last_run
          const key = `${pg.name}@${pg.version}`
          return (
            <section key={key} className="pack-card">
              <div className="pack-card__head">
                <div>
                  <h2>
                    {pg.title}{' '}
                    {/* The pack's identity, not just its display title —
                        two packs can share a title's phrasing, and the
                        operator needs to know WHICH pack a card is. */}
                    <span className="muted mono">{pg.name}@{pg.version}</span>
                  </h2>
                  <p className="muted">{pg.description}</p>
                </div>
                <span className={`pack-state pack-state--${run?.status ?? 'none'}`}>
                  {run?.status ? run.status.toUpperCase() : 'NOT BUILT'}
                  {run && run.status === 'succeeded' && !pg.current && !packs.clock_error ? ' · STALE' : ''}
                </span>
              </div>

              {!pg.eligibility.met && (
                <div className="pack-card__todo">
                  <p className="lab-sub">This pack cannot be built for this game:</p>
                  <ul>
                    {pg.eligibility.missing.map((m) => (
                      <li key={`${m.kind}-${m.name}`}>
                        <span className="mono">{m.kind} {m.name}</span>
                        {m.detail ? <span className="muted"> — {m.detail}</span> : null}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {run?.error && <div className="lab-error">{run.error}</div>}
              {run?.finished_at && (
                <p className="muted">
                  Last built {run.finished_at.slice(0, 16).replace('T', ' ')}
                  {pg.current || packs.clock_error ? '' : ' · the game’s serve tables have moved since; opening rebuilds'}
                </p>
              )}

              <div className="pack-card__actions">
                {pg.eligibility.met && (
                  <Link className="btn-link"
                        to={`/manage-insights/pack/${pathSeg(pg.name)}/${pathSeg(pg.version)}`
                            + `/${pathSeg(gameCode)}`}>
                    {pg.current ? 'Open' : run && !packs.clock_error ? 'Open (rebuilds)' : run ? 'Open' : 'Open (builds now)'}
                  </Link>
                )}
                {isAdmin && run?.has_artifact && (pg.current || run.published_at) && (
                  <button type="button" disabled={busy === key}
                          onClick={() => onPublishClick(pg.name, pg.version, Boolean(run.published_at))}>
                    {run.published_at ? 'Unpublish' : 'Publish to club'}
                  </button>
                )}
                {isAdmin && run?.published_page_current === false && pg.current && (
                  <button type="button" disabled={busy === key}
                          onClick={() => onPublishClick(pg.name, pg.version, false)}>
                    Publish this build
                  </button>
                )}
                {isAdmin && run?.published_at && pg.eligibility.met && (
                  <button type="button" disabled={busy === key}
                          onClick={() => {
                            setReason('')
                            setPending({ name: pg.name, version: pg.version,
                                         mode: 'republish' })
                          }}>
                    Rebuild & republish
                  </button>
                )}
                {run?.published_at && (
                  <span className="muted">
                    Published {run.published_at.slice(0, 10)}
                    {run.published_page_current === false && (
                      <span> · the club reads an earlier build; publish to give them this one</span>
                    )}
                  </span>
                )}
              </div>
            </section>
          )
        })}
      </section>

      {pending && (
        <div className="ip-scrim" role="dialog" aria-modal="true"
             aria-labelledby="ip-pub-title"
             onClick={() => busy || setPending(null)}>
          <div className="panel ip-confirm" onClick={(e) => e.stopPropagation()}>
            <h2 id="ip-pub-title" className="ip-confirm__title">
              {pending.mode === 'publish' ? 'Publish to the club?' : 'Rebuild and republish?'}
            </h2>
            {pending.mode === 'publish' ? (
              <p className="muted">
                This makes <strong className="mono">{pending.name}@{pending.version}</strong>{' '}
                for <strong className="mono">{gameCode}</strong> visible to the
                club’s members straight away. You can unpublish it later.
              </p>
            ) : (
              <p className="muted">
                This rebuilds <strong className="mono">{pending.name}@{pending.version}</strong>{' '}
                for <strong className="mono">{gameCode}</strong> from the latest
                data and points the club at the new page — its members see it
                straight away. A failed rebuild changes nothing: today’s page
                stays published.
              </p>
            )}
            <label className="ip-field">
              <span className="ip-label">Reason (recorded in the audit log)</span>
              <input className="ip-input" value={reason} autoFocus
                     onChange={(e) => setReason(e.target.value)}
                     onKeyDown={(e) => { if (e.key === 'Escape' && !busy) setPending(null) }} />
            </label>
            <div className="ip-confirm__actions">
              <button type="button" className="ip-confirm__go"
                      disabled={!reason.trim() || busy === `${pending.name}@${pending.version}`}
                      onClick={() => (pending.mode === 'publish'
                        ? setPublished(pending.name, pending.version, true, reason.trim())
                        : republish(pending.name, pending.version, reason.trim()))}>
                {busy
                  ? (pending.mode === 'publish' ? 'Publishing…' : 'Rebuilding…')
                  : (pending.mode === 'publish' ? 'Publish to club' : 'Rebuild & republish')}
              </button>
              <button type="button" disabled={Boolean(busy)}
                      onClick={() => setPending(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
